import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TemplateFrame } from '../../site/TemplateFrame';
import { api } from '../../lib/api';
import { Field, FormNotice } from './MemberForm';
import { messageFrom } from './memberErrors';

/**
 * Password reset, both halves on one route.
 *
 * With no `?token=` it is the "I forgot my password" form; with one it is the
 * "choose a new password" form. They are the same page because the member
 * arrives at the second from an email and at the first from a link on sign-in,
 * and splitting them would mean two routes that each look broken when reached
 * the other way round.
 *
 * The password lives in the Secrets API, so both steps proxy through our own
 * backend, which holds the service credential. Nothing here talks to
 * api.secretsfl.com directly.
 */

const MIN_LENGTH = 8;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const requestLink = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.member.requestPasswordReset(email);
      setDone(true);
    } catch (err) {
      setError(messageFrom(err, 'We could not send that email. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const setNewPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    // Checked here as well as upstream so a mismatch costs a round trip rather
    // than the token: the API only sees one password and cannot catch this.
    if (password !== confirm) {
      setError('Those passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await api.member.confirmPasswordReset(token, password);
      setDone(true);
    } catch (err) {
      setError(messageFrom(err, 'That reset link is not valid. Request a new one.'));
    } finally {
      setBusy(false);
    }
  };

  // ---- Sent, or reset. -----------------------------------------------------
  if (done) {
    return (
      <TemplateFrame title={token ? 'Password Changed' : 'Check Your Email'}>
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-6">
            <div className="member-card">
              {token ? (
                <>
                  <h3>Password changed</h3>
                  <p className="po-muted">
                    You can now sign in with your new password. It works everywhere your Secrets
                    Hideaway account does, not just here.
                  </p>
                  <Link className="button button-primary" to="/login">
                    Sign in
                  </Link>
                </>
              ) : (
                <>
                  <h3>Check your email</h3>
                  {/* Deliberately does not confirm the address exists: saying
                      "we found your account" would answer a question we are
                      careful not to answer anywhere else in this flow. */}
                  <p className="po-muted">
                    If that address has an account, a reset link is on its way. The link works once
                    and expires in an hour.
                  </p>
                  <p className="member-hint">
                    Nothing arrived? Check spam, then <Link to="/reset-password">try again</Link>.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </TemplateFrame>
    );
  }

  // ---- Choose a new password (arrived from the email). ----------------------
  if (token) {
    return (
      <TemplateFrame title="Choose a New Password">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-6">
            <div className="member-card">
              <h3>Choose a new password</h3>
              <p className="po-muted">
                This sets the password for your Secrets Hideaway account everywhere, not just on
                this site.
              </p>

              {error ? <FormNotice kind="error">{error}</FormNotice> : null}

              <form onSubmit={setNewPassword} noValidate>
                <Field
                  label="New password"
                  name="password"
                  errors={{}}
                  hint={`At least ${MIN_LENGTH} characters, with a capital letter, a number and a symbol.`}
                >
                  <input
                    id="password"
                    className="form-input"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Field>

                <Field label="Confirm new password" name="confirm" errors={{}}>
                  <input
                    id="confirm"
                    className="form-input"
                    type="password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                </Field>

                <button className="button button-primary" type="submit" disabled={busy}>
                  {busy ? 'Saving…' : 'Set new password'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </TemplateFrame>
    );
  }

  // ---- Ask for a link. -----------------------------------------------------
  return (
    <TemplateFrame title="Reset Your Password">
      <div className="row justify-content-center">
        <div className="col-md-10 col-lg-6">
          <div className="member-card">
            <h3>Reset your password</h3>
            <p className="po-muted">
              Enter the email address on your Secrets Hideaway account and we will send you a link
              to choose a new password.
            </p>

            {error ? <FormNotice kind="error">{error}</FormNotice> : null}

            <form onSubmit={requestLink} noValidate>
              <Field label="Email" name="email" errors={{}}>
                <input
                  id="email"
                  className="form-input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>

              <button className="button button-primary" type="submit" disabled={busy}>
                {busy ? 'Sending…' : 'Send reset link'}
              </button>
            </form>

            <p className="member-hint">
              Remembered it? <Link to="/login">Sign in</Link>.
            </p>
          </div>
        </div>
      </div>
    </TemplateFrame>
  );
}
